import gsap, { TimelineLite } from 'gsap';

import { BaseBigWinAnimationDrawer } from './BaseBigWinAnimationDrawer';
import { bigWinDesktopResources, bigWinRssNames } from '../../../core/gameConstants';
import { amountFormatter } from '../../../core/helpers/amountFormatter';
import { gameConfig } from '../../../core/configs';
import { FONT_BALOO_NAME } from '../../../core/constats';
import { NumberGrowService } from '../../../services/helperService/NumberGrowService';
import { partnerService } from '../../../services/helperService/PartnerService';
import { bigWinService } from '../../../services/helperService/BigWinService';
import { getTranslation, translationKeys } from '../../../core/translation/translation';
import {gameDrawer} from "../../../game/GameDrawer";

export class BigWinAnimationDesktopDrawer extends BaseBigWinAnimationDrawer {
  constructor() {
    super();
    this.container = this.createContainer('BIG_WIN');
    this.loader = this.createLoader();
    this.resources = null;
    this.loop = true;
  }

  async loadResource() {
    if (!this.resources) {
      const { res } = await super.loadResource(bigWinDesktopResources, this.loader);
      this.resources = res;
    }
    return this.resources;
  }

  setBigWinCoinTexture() {
    const texture = this.createSpine(this.resources[bigWinRssNames.bigWinDesktopCoins]);
    this.setPosition(texture, {
      x: gameConfig.width / 2,
      y: gameConfig.height,
    });
    texture.state.setAnimation(0, 'beginning', false);
    texture.state.setAnimation(1, 'loop', true);
    this.container.addChild(texture);
  }

  setBigWinWordTexture() {
    const texture = this.createSpine(this.resources[bigWinRssNames.bigWinDesktopWord]);
    this.setPosition(texture, {
      x: gameConfig.width / 2,
      y: gameConfig.height - 100,
    });

    texture.state.setAnimation(0, 'animation', false);
    this.container.addChild(texture);
  }

  setMaxWinCoins() {
    const texture = this.createSprite(this.resources[bigWinRssNames.coins]);
    const texture2 = this.createSprite(this.resources[bigWinRssNames.coins2]);
    texture.anchor.set(.5, 1);
    texture2.anchor.set(.5, 1);
    this.setPosition(texture, {
      x: gameConfig.width / 2,
      y: gameConfig.height + texture.height,
    });
    this.setPosition(texture2, {
      x: gameConfig.width / 2,
      y: gameConfig.height + texture2.height,
    });

    gsap.to(texture, {
      duration: 4,
      y: gameConfig.height,
    });
    gsap.to(texture2, {
      delay: 1,
      duration: 4,
      y: gameConfig.height,
    });
    this.container.addChild(
      texture,
      texture2,
    );
  }

  _setupHiddenAnimation(texture, index, duration) {
    new TimelineLite().to(texture, { delay: (index + 1) * duration, duration: duration / 2, alpha: 0 });
  }

  setEpicWinTexture({ index = 1, duration = 1 } = {}) {
    const texture = this.createSpine(this.resources[bigWinRssNames.epicWin]);
    texture.alpha = 0;
    this.setPosition(texture, {
      x: gameConfig.width / 2,
      y: gameConfig.height - 100,
    });
    setTimeout(() => {
      texture.alpha = 1;
      texture.state.setAnimation(0, 'animation', false);
    }, index * duration * 1000);
    this._setupHiddenAnimation(texture, index, duration);

    this.container.addChild(texture);
  }

  setMaxWinTexture({ index = 3, duration = 1 } = {}) {
    const texture = this.createSpine(this.resources[bigWinRssNames.maxWin]);
    texture.alpha = 0;
    this.setPosition(texture, {
      x: gameConfig.width / 2,
      y: gameConfig.height - 100,
    });
    setTimeout(() => {
      texture.alpha = 1;
      texture.state.setAnimation(0, 'animation', false);
    }, index * duration * 1000);

    this.container.addChild(texture);
  }

  setMegaWinTexture({ index = 2, duration = 1 } = {}) {
    const texture = this.createSpine(this.resources[bigWinRssNames.megaWin]);
    texture.alpha = 0;
    this.setPosition(texture, {
      x: gameConfig.width / 2,
      y: gameConfig.height - 100,
    });

    setTimeout(() => {
      texture.alpha = 1;
      texture.state.setAnimation(0, 'animation', false);
    }, index * duration * 1000);

    this._setupHiddenAnimation(texture, index, duration);

    this.container.addChild(texture);
  }

  setSuperWinTexture({ index = 0, duration = 1 } = {}) {
    const texture = this.createSpine(this.resources[bigWinRssNames.superWin]);
    texture.alpha = 0;
    this.setPosition(texture, {
      x: gameConfig.width / 2,
      y: gameConfig.height - 100,
    });

    setTimeout(() => {
      texture.alpha = 1;
      texture.state.setAnimation(0, 'animation', false);
    }, index * duration * 1000);

    this._setupHiddenAnimation(texture, index, duration);
    this.container.addChild(texture);
  }

  setWinAmount() {
    const amountGrow = new NumberGrowService({
      min: 0,
      max: bigWinService.amount,
      delay: 6,
      stepDelay: .05,
    });

    const rect = this.createGraphics()
      .beginFill(0x000000)
      .drawRect(
        0,
        0,
        gameConfig.width,
        gameConfig.height,
      ).endFill();

    rect.alpha = 0;

    const amountText = new this.PIXI.Text(amountGrow.min, {
      fill: [0xFFFFFF, 0xDFAC32],
      fontSize: 100,
      align: 'right',
      fontFamily: FONT_BALOO_NAME,
      stroke: 0xFFFFFF,
      strokeThickness: 3,
    });

    this.addSubscriber(amountGrow.subscribe((val) => {
      amountText.text = `${amountFormatter.format(val)} ${partnerService.displayCurrency}`;
    }));

    amountGrow.start();
    amountText.anchor.set(.5);
    this.setPosition(amountText, {
      x: gameConfig.width / 2,
      y: gameConfig.height / 2 + amountText.height / 2,
    });
    this.amountGrow = amountGrow;
    this.setupButtonMode(rect);
    this.registerClick(rect, (event) => {
      event.stopPropagation();
      this.onClick();
    });
    this.container.addChild(
      amountText,
      rect,
    );
  }

  setupPressToContinueAnimation({ texture }) {
    const duration = .5;
    return new TimelineLite()
      .to(texture, { duration, pixi: { scale: .9 } })
      .to(texture, {
        duration, pixi: { scale: 1 },
        onComplete: () => {
          if (this.loop) {
            this.setupPressToContinueAnimation({ texture });
          }
        },
      });
  }

  setPressToContinue() {
    const pressToContinue = new this.PIXI.Text(`${getTranslation(translationKeys.PRESS_ANYWHERE_TO_CONTINUE)}`, {
      fontSize: 35,
      fill: 0xf7e0b4,
      fontFamily: FONT_BALOO_NAME,
      align: 'center',
    });
    pressToContinue.anchor.set(.5);
    this.setPosition(pressToContinue, {
      x: gameConfig.width / 2,
      y: gameConfig.height / 2 + 180,
    });
    this.setupPressToContinueAnimation({ texture: pressToContinue });
    this.container.addChild(pressToContinue);
  }

  setMaxWinText() {
    const text = new this.PIXI.Text(`${getTranslation(translationKeys.BUY_BONUS)}`, {
      fontSize: 35,
      fill: 0xf7e0b4,
      fontFamily: FONT_BALOO_NAME,
      align: 'center',
    });
    text.anchor.set(.5);
    this.setPosition(text, {
      x: gameConfig.width / 2,
      y: gameConfig.height / 2 + 130,
    });
    this.container.addChild(text);
  }

  setCongratulationsText() {
    const congratulations = new this.PIXI.Text(`${getTranslation(translationKeys.CONGRATULATIONS)} !!!`, {
      fontSize: 45,
      fill: 0xf7e0b4,
      fontFamily: FONT_BALOO_NAME,
      align: 'center',
    });

    const youHaveWon = new this.PIXI.Text(`${getTranslation(translationKeys.YOU_HAVE_WON)} :`, {
      fontSize: 45,
      fill: 0xf7e0b4,
      fontFamily: FONT_BALOO_NAME,
      align: 'center',
    });
    congratulations.anchor.set(.5);
    youHaveWon.anchor.set(.5);
    this.setPosition(congratulations, {
      x: gameConfig.width / 2,
      y: gameConfig.height / 2 - 60,
    });
    this.setPosition(youHaveWon, {
      x: gameConfig.width / 2,
      y: gameConfig.height / 2 - 10,
    });
    this.container.addChild(
      congratulations,
      youHaveWon,
    );
  }

  setupSubscriber() {
    this.addSubscriber(bigWinService.subscribeState(state => {
      if (state) {
        this.startBigWin();
      } else {
        bigWinService.isMaxWin = false;
        this.loop = false;
        this.container.children = [];
      }
    }, true));
  }

  startBigWin() {
    this.container.children = [];
    this.loop = true;
    const duration = 1;
    this.setBigWinCoinTexture();
    if (bigWinService.isMaxWin) {
      this.setSuperWinTexture({ index: 0, duration });
      this.setEpicWinTexture({ index: 1, duration });
      this.setMegaWinTexture({ index: 2, duration });
      this.setMaxWinTexture({ index: 3, duration });
      this.setMaxWinText();
      this.setMaxWinCoins();
    } else {
      this.setBigWinWordTexture();
    }
    this.setWinAmount();
    this.setPressToContinue();
    this.setCongratulationsText();
  }

  async onLoad() {
    return this.loadResource();
  }

  async executeBigWinAnimation({ root }) {
    await this.loadResource();
    this.setupSubscriber();
    root.addChild(this.container);
  }
}
